import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CalendarClock, AlertTriangle, Loader2, ArrowRight, Star, ShieldCheck, Info } from "lucide-react";

export const ClosureGuard = ({ trip, editable = false, onTripChange }) => {
  const startDate = trip._request?.travel_start_date || "";
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState({});
  const debounce = useRef();

  const runCheck = useCallback(async (t) => {
    if (!t._request?.travel_start_date) {
      setResult(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/trips/check-closures", { trip: t });
      setResult(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runCheck(trip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const setDate = (val) => {
    const next = { ...trip, _request: { ...(trip._request || {}), travel_start_date: val } };
    onTripChange?.(next, { label: "Set travel dates" });
  };

  const applyAlternative = (f, alt) => {
    const newItin = trip.itinerary.map((d, di) => {
      if (di !== f.day_index) return d;
      const acts = (d.activities || []).map((a, ai) =>
        ai === f.activity_index
          ? {
              ...a,
              name: alt.name,
              lat: alt.lat,
              lng: alt.lng,
              original_name: a.name,
              substituted: true,
              verified: true,
              closed_permanently: false,
              google: alt,
            }
          : a
      );
      return { ...d, activities: acts };
    });
    onTripChange?.({ ...trip, itinerary: newItin }, { label: "Closed-day swap" });
    toast.success(`Swapped in ${alt.name}`);
    setTimeout(() => runCheck({ ...trip, itinerary: newItin }), 300);
  };

  const findings = (result?.findings || []).filter((f) => !dismissed[`${f.day_index}-${f.activity_index}`]);
  const uncertain = result?.uncertain || [];

  return (
    <section data-testid="closure-guard" className="rounded-2xl border border-black/5 bg-white/60 p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-2xl flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-[#C8553D]" /> Travel dates & opening check
        </h3>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#C8553D]" />}
          <label className="text-sm text-muted-foreground">Trip start</label>
          <input
            data-testid="trip-start-date"
            type="date"
            value={startDate}
            disabled={!onTripChange}
            onChange={(e) => setDate(e.target.value)}
            className="text-sm rounded-full border border-black/10 bg-white px-3 py-1.5 outline-none focus:border-[#1A3B2B]"
          />
        </div>
      </div>

      {!startDate && (
        <p className="mt-3 text-sm text-muted-foreground">Add your trip start date and we'll check every stop is actually open on the day you visit — and suggest verified alternatives for anything closed.</p>
      )}

      {startDate && findings.length === 0 && !loading && (
        <p data-testid="closure-all-clear" className="mt-3 text-sm text-[#1A3B2B] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Every verified stop looks open on your scheduled days.
        </p>
      )}

      {findings.length > 0 && (
        <div className="mt-4 space-y-4" data-testid="closure-findings">
          {findings.map((f) => (
            <div key={`${f.day_index}-${f.activity_index}`} className="rounded-xl border border-[#C8553D]/30 bg-[#C8553D]/5 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#C8553D] shrink-0 mt-0.5" />
                <p className="text-sm">
                  <span className="font-medium">Day {f.day} ({f.weekday}):</span> {f.name} — {f.reason}.
                </p>
              </div>
              {f.alternatives?.length > 0 ? (
                <div className="mt-3 pl-6">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Verified open alternatives nearby</p>
                  <div className="flex flex-wrap gap-2">
                    {f.alternatives.map((alt) => (
                      <button
                        key={alt.place_id}
                        data-testid="closure-alt-btn"
                        onClick={() => applyAlternative(f, alt)}
                        disabled={!onTripChange}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1A3B2B] text-[#1A3B2B] text-sm hover:bg-[#1A3B2B] hover:text-[#FDFBF7] transition-colors disabled:opacity-50"
                      >
                        {alt.name}
                        {alt.rating ? <span className="inline-flex items-center gap-0.5 opacity-80"><Star className="w-3 h-3" />{alt.rating}</span> : null}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-2 pl-6 text-xs text-muted-foreground">No open alternative found nearby — consider moving this to another day.</p>
              )}
              <button
                onClick={() => setDismissed((d) => ({ ...d, [`${f.day_index}-${f.activity_index}`]: true }))}
                className="mt-2 pl-6 text-xs text-muted-foreground hover:text-foreground"
              >
                Keep it anyway
              </button>
            </div>
          ))}
        </div>
      )}

      {startDate && uncertain.length > 0 && (
        <p data-testid="closure-uncertain" className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Opening hours are unconfirmed for {uncertain.map((u) => u.name).join(", ")} — please double-check before you go.
        </p>
      )}
    </section>
  );
};
